import React from 'react';
import { useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import { PageHeader, SectionContent, StyledList } from '../../components';
import { TAX_STRATEGY } from '../../data';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';

const TaxStrategyPage = ({ setRefs }) => {
  const taxStrategyRef = useRef(null);
  const riskManagementRef = useRef(null);
  const taxPlanningRef = useRef(null);
  const riskToleranceRef = useRef(null);
  const hmrcRelationshipRef = useRef(null);

  const refs = useMemo(
    () => ({
      taxStrategy: taxStrategyRef,
      riskManagement: riskManagementRef,
      taxPlanning: taxPlanningRef,
      riskTolerance: riskToleranceRef,
      hmrcRelationship: hmrcRelationshipRef
    }),
    []
  );

  // Update global refs if setRefs function is provided
  React.useEffect(() => {
    if (setRefs && typeof setRefs === 'function') {
      setRefs(refs);
    }
  }, [refs, setRefs]);

  return (
    <>
      <SEO
        title={seoData.taxStrategy.title}
        content={seoData.taxStrategy.content}
        keywords={seoData.taxStrategy.keywords}
        canonical={seoData.taxStrategy.canonical}
        ogImage={seoData.taxStrategy.ogImage}
      />
      {/* Page Header */}}
      <PageHeader
        id='hero'
        title={TAX_STRATEGY.TITLE}
        align='left'
        sx={{ mb: SECTION_SPACING }}
      />
      {/* Introduction */}
      <SectionContent
        id='taxStrategy'
        ref={refs.taxStrategy}
        initialSpacing={false}
        text={[
          {
            text: TAX_STRATEGY.LAST_UPDATED,
            marginBottom: ELEMENT_SPACING,
            fontStyle: 'italic',
            fontSize: '1.1rem',
            color: THEME.COLORS.NEUTRAL_GRAY_DARK
          },
          ...TAX_STRATEGY.INTRO.map(paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }))
        ]}
      />
      {/* How MarQ Capital Manages Fiscal Risk */}
      <SectionContent
        id='riskManagement'
        ref={refs.riskManagement}
        title={TAX_STRATEGY.SECTIONS.RISK_MANAGEMENT.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[]}
      />
      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList items={TAX_STRATEGY.SECTIONS.RISK_MANAGEMENT.LIST_ITEMS} />
      </Box>
      {/* Our Approach to Tax Planning */}
      <SectionContent
        id='taxPlanning'
        ref={refs.taxPlanning}
        title={TAX_STRATEGY.SECTIONS.TAX_PLANNING.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[]}
      />
      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList items={TAX_STRATEGY.SECTIONS.TAX_PLANNING.LIST_ITEMS} />
      </Box>
      {/* Fiscal Risk Tolerance */}
      <SectionContent
        id='riskTolerance'
        ref={refs.riskTolerance}
        title={TAX_STRATEGY.SECTIONS.RISK_TOLERANCE.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[]}
      />
      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList items={TAX_STRATEGY.SECTIONS.RISK_TOLERANCE.LIST_ITEMS} />
      </Box>
      {/* Relationship with HMRC */}
      <SectionContent
        id='hmrcRelationship'
        ref={refs.hmrcRelationship}
        title={TAX_STRATEGY.SECTIONS.HMRC_RELATIONSHIP.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[]}
      />
      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList
          items={TAX_STRATEGY.SECTIONS.HMRC_RELATIONSHIP.LIST_ITEMS}
        />
      </Box>
      {/* Footer note with separator line */}
      <Box
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          mt: 4,
          mb: 2
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1rem',
            fontStyle: 'italic',
            color: THEME.COLORS.NEUTRAL_GRAY,
            lineHeight: 1.6,
            display: 'block'
          }}
        >
          {TAX_STRATEGY.FOOTER_NOTE}
        </Typography>
      </Box>
    </>
  );
};

export default TaxStrategyPage;
